import React, { createContext, useContext, useReducer, useCallback, useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { Cliente, Contenuto, StatoContenuto, PiattaformaSocial, EventoCalendario, DashboardStats, ClientStats, ContentStats, ContentFilters } from '../types';
import { useAuth } from './AuthContext';
import { crashLogger } from '../utils/crashLogger';
import { formatDateForBackend, safeSerializeDate } from '../utils/dateUtils';
import { campiPubblica } from '../components/BadgePubblica';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

// Backend client data format
interface BackendClient {
  id: string;
  company_name: string;
  contact_person: string;
  phone: string;
  email: string;
  monthly_reels: number;
  copy_by_client?: boolean;
  graphic_posts_enabled?: boolean;
  graphic_posts_per_week?: number;
  photo_carousels_per_week?: number;
  graphic_post_days?: string[] | null;
  alternanza_fase?: number | null;
  publish_accounts?: string[] | null;
  preferred_recording_days: string[] | string | null | undefined;
  platforms: string[] | string | null | undefined;
  reel_formats: string[] | null;
  status: string;
  created_at: string | Date;
  updated_at: string | Date;
  instagram_username?: string;
  instagram_password?: string;
  tiktok_username?: string;
  tiktok_password?: string;
  youtube_channel?: string;
  youtube_password?: string;
  facebook_page?: string;
  facebook_password?: string;
  linkedin_page?: string;
  linkedin_password?: string;
  google_drive_link?: string;
  web_app_lead_link?: string;
  logo_url?: string;
  weekly_published_days?: string[] | null;
  weekly_published_channels?: Record<string, string[]> | null;
  publish_time?: string | null;
}

// Nome pagina/account + password di ogni social, dal formato backend a quello
// del frontend (usato sia dalla lista clienti che dalle risposte create/update)
const mapSocialAccountsToFrontend = (client: BackendClient) => ({
  nomeInstagram: client.instagram_username,
  passwordInstagram: client.instagram_password,
  nomeTikTok: client.tiktok_username,
  passwordTikTok: client.tiktok_password,
  nomeYouTube: client.youtube_channel,
  passwordYouTube: client.youtube_password,
  nomeFacebook: client.facebook_page,
  passwordFacebook: client.facebook_password,
  nomeLinkedIn: client.linkedin_page,
  passwordLinkedIn: client.linkedin_password
});

interface DataState {
  clienti: Cliente[];
  contenuti: Contenuto[];
  eventiCalendario: EventoCalendario[];
  dashboardStats: DashboardStats;
  clientStats: ClientStats;
  contentStats: ContentStats;
}

const initialState: DataState = {
  clienti: [],
  contenuti: [],
  eventiCalendario: [],
  dashboardStats: {} as DashboardStats,
  clientStats: {} as ClientStats,
  contentStats: {} as ContentStats
};

type Action =
  | { type: 'SET_CLIENTI'; payload: Cliente[] }
  | { type: 'ADD_CLIENTE'; payload: Cliente }
  | { type: 'UPDATE_CLIENTE'; payload: Partial<Cliente> }
  | { type: 'SET_WEEKLY_PUBLISHED'; payload: { id: string; giorni: string[]; canali: Record<string, string[]> } }
  | { type: 'DELETE_CLIENTE'; payload: string }
  | { type: 'SET_CONTENUTI'; payload: Contenuto[] }
  | { type: 'ADD_CONTENUTO'; payload: Contenuto }
  | { type: 'UPDATE_CONTENUTO'; payload: Contenuto }
  | { type: 'DELETE_CONTENUTO'; payload: string }
  | { type: 'SET_EVENTI'; payload: EventoCalendario[] }
  | { type: 'SET_DASHBOARD_STATS'; payload: DashboardStats }
  | { type: 'SET_CLIENT_STATS'; payload: ClientStats }
  | { type: 'SET_CONTENT_STATS'; payload: ContentStats };

const DataContext = createContext<{
  state: DataState;
  dispatch: React.Dispatch<Action>;
  isLoading: boolean;
  addCliente: (cliente: Omit<Cliente, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateCliente: (id: string, updates: Partial<Cliente>) => Promise<void>;
  setPubblicazioneSettimanale: (id: string, day: string, channels: string[]) => Promise<void>;
  deleteCliente: (id: string) => Promise<void>;
  archiveCliente: (id: string) => Promise<void>;
  loadClienti: () => Promise<void>;
  loadDashboardStats: () => Promise<void>;
  addContenuto: (contenuto: Omit<Contenuto, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateContenuto: (id: string, updates: Partial<Contenuto>) => Promise<void>;
  deleteContenuto: (id: string) => Promise<void>;
  updateContenutoStatus: (id: string, status: StatoContenuto, notes?: string) => Promise<void>;
  publishContenuto: (id: string, linkPubblicato: string) => Promise<void>;
  cancelScheduling: (id: string, notes?: string) => Promise<void>;
  unarchiveContenuto: (id: string) => Promise<void>;
  confermaUscita: (id: string) => Promise<void>;
  toggleVideoRecorded: (id: string) => Promise<void>;
  loadContenuti: () => Promise<void>;
  loadContenutiFiltered: (filters: ContentFilters) => Promise<void>;
  getClienteById: (id: string) => Cliente | undefined;
} | undefined>(undefined);

const reducer = (state: DataState, action: Action): DataState => {
  switch (action.type) {
    case 'SET_CLIENTI':
      return { ...state, clienti: action.payload };
    case 'ADD_CLIENTE':
      return { 
        ...state, 
        clienti: [...state.clienti, action.payload].sort((a, b) => 
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        )
      };
    case 'UPDATE_CLIENTE':
      return {
        ...state,
        clienti: state.clienti.map(cliente =>
          cliente.id === action.payload.id
            ? { ...cliente, ...action.payload, updatedAt: new Date() }
            : cliente
        ).sort((a, b) => 
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        )
      };
    case 'SET_WEEKLY_PUBLISHED':
      // Aggiorna SOLO il flag di pubblicazione: nessun re-sort e nessun tocco a
      // updatedAt, così spuntando la checkbox il cliente non salta in cima.
      return {
        ...state,
        clienti: state.clienti.map(cliente =>
          cliente.id === action.payload.id
            ? {
                ...cliente,
                pubblicazioneSettimanaleGiorni: action.payload.giorni,
                pubblicazioneSettimanaleCanali: action.payload.canali
              }
            : cliente
        )
      };
    case 'DELETE_CLIENTE':
      return {
        ...state,
        clienti: state.clienti.filter(cliente => cliente.id !== action.payload)
      };
    case 'SET_CONTENUTI':
      return { 
        ...state, 
        contenuti: action.payload.sort((a, b) => 
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        )
      };
    case 'ADD_CONTENUTO':
      return { 
        ...state, 
        contenuti: [action.payload, ...state.contenuti].sort((a, b) => 
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        )
      };
    case 'UPDATE_CONTENUTO':
      const updatedContenuti = state.contenuti.map(contenuto =>
        contenuto.id === action.payload.id
          ? { ...contenuto, ...action.payload }
          : contenuto
      );
      return {
        ...state,
        contenuti: updatedContenuti.sort((a, b) => 
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        )
      };
    case 'DELETE_CONTENUTO':
      return {
        ...state,
        contenuti: state.contenuti.filter(contenuto => contenuto.id !== action.payload)
      };
    case 'SET_DASHBOARD_STATS':
      return { ...state, dashboardStats: action.payload };
    case 'SET_CLIENT_STATS':
      return { ...state, clientStats: action.payload };
    case 'SET_CONTENT_STATS':
      return { ...state, contentStats: action.payload };
    default:
      return state;
  }
};

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [isLoading, setIsLoading] = useState(false);
  const { getToken, isAuthenticated, handleApiError } = useAuth();

  // Flags per evitare chiamate API duplicate
  const [isLoadingClienti, setIsLoadingClienti] = useState(false);
  const [isLoadingContenuti, setIsLoadingContenuti] = useState(false);
  // Guardia "un caricamento alla volta" su ref, non sullo stato: la useCallback
  // di loadContenuti si rigenera solo quando cambia getToken, quindi leggere
  // isLoadingContenuti dalla closure la congelava. Se il provider di
  // autenticazione si ridisegnava durante un caricamento, la funzione
  // restava con isLoadingContenuti=true e da lì saltava in silenzio ogni
  // ricaricamento (17/09/2026: bozza salvata dall'AI Assistant che non
  // compariva negli Script attuali fino al reload della pagina).
  const contenutiInCaricamento = useRef(false);
  // Una richiesta arrivata durante un caricamento non si perde: si rifà appena finito.
  const ricaricaContenutiDopo = useRef(false);
  const loadContenutiRef = useRef<() => Promise<void>>(async () => {});
  const [dataLoaded, setDataLoaded] = useState(false);

  // Helper function for authenticated API calls with automatic retry on 401
  const authenticatedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
    const makeRequest = async (token: string) => {
      return fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
          'Authorization': `Bearer ${token}`
        }
      });
    };

    const currentToken = getToken();
    if (!currentToken) {
      throw new Error('No authentication token available');
    }

    const response = await makeRequest(currentToken);

    if (response.status === 401) {
      console.log('🚨 Token expired, triggering handleApiError...');
      await handleApiError(response);

      // Try again with potentially refreshed token
      const newToken = getToken();
      if (newToken && newToken !== currentToken) {
        console.log('✅ Retrying with refreshed token');
        return makeRequest(newToken);
      }
    }

    return response;
  };

  const loadClienti = useCallback(async () => {
    // Evita chiamate duplicate
    if (isLoadingClienti) {
      // Non loggare ogni skip, solo occasionalmente
      if (Math.random() < 0.1) {
        crashLogger.logEvent('LOAD_CLIENTI_SKIPPED', { reason: 'Already loading' });
      }
      return;
    }
    
    try {
      setIsLoadingClienti(true);
      setIsLoading(true);
      
      crashLogger.logEvent('LOAD_CLIENTI_START', {
        hasToken: !!getToken()
      });
      
      // Load all clients (active and archived) with a valid limit
      const response = await fetch(`${API_BASE_URL}/clients?limit=100&status=active`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      crashLogger.logEvent('LOAD_CLIENTI_RESPONSE', {
        status: response.status,
        ok: response.ok
      });

      if (response.ok) {
        const data = await response.json();
        console.log('🔍 loadClienti - Risposta dal backend:', data);
        
        if (data.success) {
          // Transform backend data to frontend format
          const transformedClients = data.data.map((client: BackendClient) => {
            console.log('🔍 loadClienti - Cliente grezzo dal backend:', {
              id: client.id,
              company_name: client.company_name,
              google_drive_link: client.google_drive_link
            });

            return {
              id: client.id,
              nomeAzienda: client.company_name,
              referenteRiprese: client.contact_person,
              telefono: client.phone,
              email: client.email,
              reelMensili: client.monthly_reels,
              testiDalCliente: !!client.copy_by_client,
              postGrafici: !!client.graphic_posts_enabled,
              postGraficiSettimana: client.graphic_posts_enabled ? (client.graphic_posts_per_week ?? 0) : 0,
              caroselliFotoSettimana: client.graphic_posts_enabled ? (client.photo_carousels_per_week ?? 0) : 0,
              giorniPost: mapDaysToFrontend(client.graphic_post_days),
              alternanzaFase: client.alternanza_fase ?? null,
              accountPubblicazione: client.publish_accounts || [],
              giorniPreferitiRiprese: mapDaysToFrontend(client.preferred_recording_days),
              piattaforme: mapPlatformsToFrontend(client.platforms),
              formatReel: client.reel_formats || [],
              pubblicazioneSettimanaleGiorni: client.weekly_published_days || [],
              pubblicazioneSettimanaleCanali: client.weekly_published_channels || {},
              orarioPubblicazione: client.publish_time || '21:00',
              status: client.status || 'active',
              createdAt: new Date(client.created_at),
              updatedAt: new Date(client.updated_at),
              ...mapSocialAccountsToFrontend(client),
              linkGoogleDrive: client.google_drive_link,
              linkWebAppLead: client.web_app_lead_link,
              logoUrl: client.logo_url
            };
          });
          
          console.log('🔍 loadClienti - Clienti trasformati:', transformedClients.map((client: { id: string; nomeAzienda: string; linkGoogleDrive?: string }) => ({
            id: client.id,
            nomeAzienda: client.nomeAzienda,
            linkGoogleDrive: client.linkGoogleDrive
          })));
          
          // Also load archived clients
          const archivedResponse = await fetch(`${API_BASE_URL}/clients?limit=100&status=inactive`, {
            headers: {
              'Authorization': `Bearer ${getToken()}`
            }
          });
          
          if (archivedResponse.ok) {
            const archivedData = await archivedResponse.json();
            if (archivedData.success) {
              const transformedArchivedClients = archivedData.data.map((client: BackendClient) => ({
                id: client.id,
                nomeAzienda: client.company_name,
                referenteRiprese: client.contact_person,
                telefono: client.phone,
                email: client.email,
                reelMensili: client.monthly_reels,
                testiDalCliente: !!client.copy_by_client,
                postGrafici: !!client.graphic_posts_enabled,
                postGraficiSettimana: client.graphic_posts_enabled ? (client.graphic_posts_per_week ?? 0) : 0,
                caroselliFotoSettimana: client.graphic_posts_enabled ? (client.photo_carousels_per_week ?? 0) : 0,
              giorniPost: mapDaysToFrontend(client.graphic_post_days),
              alternanzaFase: client.alternanza_fase ?? null,
                accountPubblicazione: client.publish_accounts || [],
                giorniPreferitiRiprese: mapDaysToFrontend(client.preferred_recording_days),
                piattaforme: mapPlatformsToFrontend(client.platforms),
                formatReel: client.reel_formats || [],
                status: 'archived',
                createdAt: new Date(client.created_at),
                updatedAt: new Date(client.updated_at),
                ...mapSocialAccountsToFrontend(client),
                linkGoogleDrive: client.google_drive_link,
                linkWebAppLead: client.web_app_lead_link,
                logoUrl: client.logo_url
              }));
              
              transformedClients.push(...transformedArchivedClients);
            }
          }
          
          dispatch({
            type: 'SET_CLIENTI',
            payload: transformedClients
          });
        }
      } else {
        console.error('Failed to load clients:', response.status, response.statusText);
        toast.error('Errore durante il caricamento dei clienti');
      }
    } catch (error) {
      console.error('Error loading clients:', error);
      toast.error('Errore durante il caricamento dei clienti');
    } finally {
      setIsLoading(false);
      setIsLoadingClienti(false);
    }
  }, [getToken]);

  // Helper functions for status mapping
  const mapStatusToBackend = (frontendStatus: string): string => {
    const statusMap: { [key: string]: string } = {
      'bozza': 'draft',
      'testo_pronto': 'text_ready',
      'registrato': 'recorded',
      'montato': 'edited',
      'programmato': 'scheduled',
      'pubblicato': 'published',
      'archiviato': 'archived'
    };
    return statusMap[frontendStatus] || frontendStatus;
  };

  const mapStatusToFrontend = (backendStatus: string): StatoContenuto => {
    const statusMap: { [key: string]: StatoContenuto } = {
      'draft': StatoContenuto.BOZZA,
      'text_ready': StatoContenuto.TESTO_PRONTO,
      'recorded': StatoContenuto.REGISTRATO,
      'edited': StatoContenuto.MONTATO,
      'scheduled': StatoContenuto.PROGRAMMATO,
      'published': StatoContenuto.PUBBLICATO,
      'archived': StatoContenuto.ARCHIVIATO
    };
    return statusMap[backendStatus] || StatoContenuto.BOZZA;
  };

  // Helper function to map frontend platform names to backend format
  const mapPlatformsToBackend = (platforms: string[]): string[] => {
    const platformMap: { [key: string]: string } = {
      'Facebook': 'facebook',
      'Instagram': 'instagram',
      'TikTok': 'tiktok',
      'YouTube': 'youtube',
      'LinkedIn': 'linkedin'
    };
    return platforms.map(platform => platformMap[platform] || platform.toLowerCase());
  };

  // Helper function to map backend platform names to frontend format
  const mapPlatformsToFrontend = (platforms: string[] | string | null | undefined): PiattaformaSocial[] => {
    let platformArray: string[] = [];

    if (!platforms) {
      return [];
    }

    // Handle PostgreSQL array format like "{instagram,tiktok}"
    if (typeof platforms === 'string' && platforms.startsWith('{') && platforms.endsWith('}')) {
      const cleaned = platforms.slice(1, -1); // Remove { and }
      platformArray = cleaned ? cleaned.split(',') : [];
    } else if (Array.isArray(platforms)) {
      platformArray = platforms;
    } else {
      console.warn('mapPlatformsToFrontend: unsupported format', platforms);
      return [];
    }

    const platformMap: { [key: string]: PiattaformaSocial } = {
      'facebook': PiattaformaSocial.FACEBOOK,
      'instagram': PiattaformaSocial.INSTAGRAM,
      'tiktok': PiattaformaSocial.TIKTOK,
      'youtube': PiattaformaSocial.YOUTUBE,
      'linkedin': PiattaformaSocial.LINKEDIN
    };

    return platformArray.map(platform => {
      const trimmed = platform.trim(); // Remove any whitespace
      return platformMap[trimmed] || (trimmed as PiattaformaSocial);
    });
  };

  // Helper function to map Italian day names to English (lowercase as required by backend)
  const mapDaysToBackend = (days: string[]): string[] => {
    const dayMap: { [key: string]: string } = {
      'Lunedì': 'monday',
      'Martedì': 'tuesday',
      'Mercoledì': 'wednesday',
      'Giovedì': 'thursday',
      'Venerdì': 'friday',
      'Sabato': 'saturday',
      'Domenica': 'sunday'
    };
    return days.map(day => dayMap[day] || day);
  };

  // Helper function to map English day names (lowercase from backend) to Italian
  const mapDaysToFrontend = (days: string[] | string | null | undefined): string[] => {
    let daysArray: string[] = [];
    
    if (!days) {
      return [];
    }
    
    // Handle PostgreSQL array format like "{monday,tuesday}"
    if (typeof days === 'string' && days.startsWith('{') && days.endsWith('}')) {
      const cleaned = days.slice(1, -1); // Remove { and }
      daysArray = cleaned ? cleaned.split(',') : [];
    } else if (Array.isArray(days)) {
      daysArray = days;
    } else {
      console.warn('mapDaysToFrontend: unsupported format', days);
      return [];
    }
    
    const dayMap: { [key: string]: string } = {
      'monday': 'Lunedì',
      'tuesday': 'Martedì',
      'wednesday': 'Mercoledì',
      'thursday': 'Giovedì',
      'friday': 'Venerdì',
      'saturday': 'Sabato',
      'sunday': 'Domenica'
    };
    
    return daysArray.map(day => {
      const trimmed = day.trim(); // Remove any whitespace
      return dayMap[trimmed] || trimmed;
    });
  };

  const addCliente = async (clienteData: Omit<Cliente, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      setIsLoading(true);
      
      // Transform frontend data structure to backend format
      const backendData = {
        company_name: clienteData.nomeAzienda,
        contact_person: clienteData.referenteRiprese,
        phone: clienteData.telefono,
        email: clienteData.email,
        monthly_reels: clienteData.reelMensili,
        copy_by_client: !!clienteData.testiDalCliente,
        graphic_posts_enabled: !!clienteData.postGrafici,
        graphic_posts_per_week: clienteData.postGrafici ? (clienteData.postGraficiSettimana ?? 0) : 0,
        photo_carousels_per_week: clienteData.postGrafici ? (clienteData.caroselliFotoSettimana ?? 0) : 0,
        publish_accounts: clienteData.accountPubblicazione || [],
        preferred_recording_days: mapDaysToBackend(clienteData.giorniPreferitiRiprese),
        platforms: mapPlatformsToBackend(clienteData.piattaforme),
        reel_formats: clienteData.formatReel,
        publish_time: clienteData.orarioPubblicazione || '21:00',
        // In creazione i campi vuoti non vengono inviati (restano NULL a DB)
        instagram_username: clienteData.nomeInstagram || undefined,
        instagram_password: clienteData.passwordInstagram || undefined,
        tiktok_username: clienteData.nomeTikTok || undefined,
        tiktok_password: clienteData.passwordTikTok || undefined,
        youtube_channel: clienteData.nomeYouTube || undefined,
        youtube_password: clienteData.passwordYouTube || undefined,
        facebook_page: clienteData.nomeFacebook || undefined,
        facebook_password: clienteData.passwordFacebook || undefined,
        linkedin_page: clienteData.nomeLinkedIn || undefined,
        linkedin_password: clienteData.passwordLinkedIn || undefined,
        google_drive_link: clienteData.linkGoogleDrive,
        web_app_lead_link: clienteData.linkWebAppLead
      };

      console.log('Sending client data to backend:', JSON.stringify(backendData, null, 2));
      
      const response = await fetch(`${API_BASE_URL}/clients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(backendData)
      });

      console.log('Backend response status:', response.status);
      console.log('Backend response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('Backend validation error:', errorData);
        if (errorData.errors && Array.isArray(errorData.errors)) {
          console.error('Validation errors details:', errorData.errors);
          errorData.errors.forEach((error: any, index: number) => {
            console.error(`Error ${index + 1}:`, error);
          });
        }
        throw new Error(errorData.message || `API endpoint not found (${response.status})`);
      }

      const data = await response.json();
      
      // Transform backend response to frontend format
      const frontendClient: Cliente = {
        id: data.data.id,
        nomeAzienda: data.data.company_name,
        referenteRiprese: data.data.contact_person,
        telefono: data.data.phone,
        email: data.data.email,
        reelMensili: data.data.monthly_reels,
        testiDalCliente: !!data.data.copy_by_client,
        postGrafici: !!data.data.graphic_posts_enabled,
        postGraficiSettimana: data.data.graphic_posts_enabled ? (data.data.graphic_posts_per_week ?? 0) : 0,
        caroselliFotoSettimana: data.data.graphic_posts_enabled ? (data.data.photo_carousels_per_week ?? 0) : 0,
        giorniPost: mapDaysToFrontend(data.data.graphic_post_days),
        alternanzaFase: data.data.alternanza_fase ?? null,
        accountPubblicazione: data.data.publish_accounts || [],
        giorniPreferitiRiprese: mapDaysToFrontend(data.data.preferred_recording_days),
        piattaforme: mapPlatformsToFrontend(data.data.platforms),
        formatReel: data.data.reel_formats || [],
        status: data.data.status || 'active',
        createdAt: new Date(data.data.created_at),
        updatedAt: new Date(data.data.updated_at),
        ...mapSocialAccountsToFrontend(data.data),
        linkGoogleDrive: data.data.google_drive_link,
        linkWebAppLead: data.data.web_app_lead_link,
        logoUrl: data.data.logo_url
      };

      dispatch({
        type: 'ADD_CLIENTE',
        payload: frontendClient
      });

      toast.success('Cliente aggiunto con successo');
    } catch (error) {
      console.error('Error adding client:', error);
      toast.error('Errore durante l\'aggiunta del cliente');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateCliente = async (id: string, updates: Partial<Cliente>) => {
    try {
      console.log('🔍 updateCliente chiamata con:', { id, updates });
      setIsLoading(true);
      
      // Transform frontend data structure to backend format
      const backendUpdates: any = {};
      
      if (updates.nomeAzienda) backendUpdates.company_name = updates.nomeAzienda;
      if (updates.referenteRiprese !== undefined) backendUpdates.contact_person = updates.referenteRiprese;
      if (updates.telefono) backendUpdates.phone = updates.telefono;
      if (updates.email !== undefined) backendUpdates.email = updates.email;
      if (updates.reelMensili) backendUpdates.monthly_reels = updates.reelMensili;
      if (updates.testiDalCliente !== undefined) backendUpdates.copy_by_client = updates.testiDalCliente;
      if (updates.postGrafici !== undefined) backendUpdates.graphic_posts_enabled = updates.postGrafici;
      if (updates.postGraficiSettimana !== undefined) backendUpdates.graphic_posts_per_week = updates.postGraficiSettimana;
      if (updates.caroselliFotoSettimana !== undefined) backendUpdates.photo_carousels_per_week = updates.caroselliFotoSettimana;
      if (updates.accountPubblicazione !== undefined) backendUpdates.publish_accounts = updates.accountPubblicazione;
      if (updates.giorniPreferitiRiprese) backendUpdates.preferred_recording_days = mapDaysToBackend(updates.giorniPreferitiRiprese);
      if (updates.piattaforme) backendUpdates.platforms = mapPlatformsToBackend(updates.piattaforme);
      if (updates.formatReel) backendUpdates.reel_formats = updates.formatReel;
      if (updates.orarioPubblicazione !== undefined) backendUpdates.publish_time = updates.orarioPubblicazione;
      if (updates.nomeInstagram !== undefined) backendUpdates.instagram_username = updates.nomeInstagram;
      if (updates.passwordInstagram !== undefined) backendUpdates.instagram_password = updates.passwordInstagram;
      if (updates.nomeTikTok !== undefined) backendUpdates.tiktok_username = updates.nomeTikTok;
      if (updates.passwordTikTok !== undefined) backendUpdates.tiktok_password = updates.passwordTikTok;
      if (updates.nomeYouTube !== undefined) backendUpdates.youtube_channel = updates.nomeYouTube;
      if (updates.passwordYouTube !== undefined) backendUpdates.youtube_password = updates.passwordYouTube;
      if (updates.nomeFacebook !== undefined) backendUpdates.facebook_page = updates.nomeFacebook;
      if (updates.passwordFacebook !== undefined) backendUpdates.facebook_password = updates.passwordFacebook;
      if (updates.nomeLinkedIn !== undefined) backendUpdates.linkedin_page = updates.nomeLinkedIn;
      if (updates.passwordLinkedIn !== undefined) backendUpdates.linkedin_password = updates.passwordLinkedIn;
      // Always include linkGoogleDrive, even if it's empty
      if ('linkGoogleDrive' in updates) {
        console.log('✅ Aggiungendo linkGoogleDrive al backendUpdates:', updates.linkGoogleDrive);
        console.log('🔍 Tipo di linkGoogleDrive:', typeof updates.linkGoogleDrive);
        console.log('🔍 linkGoogleDrive è vuoto?', updates.linkGoogleDrive === '');
        backendUpdates.google_drive_link = updates.linkGoogleDrive;
      } else {
        console.log('❌ linkGoogleDrive non è presente negli updates');
      }
      // Always include linkWebAppLead, even if it's empty
      if ('linkWebAppLead' in updates) {
        backendUpdates.web_app_lead_link = updates.linkWebAppLead;
      }
      if (updates.status !== undefined) backendUpdates.status = updates.status;
      if ('logoUrl' in updates) backendUpdates.logo_url = updates.logoUrl;
      
      console.log('🔍 backendUpdates finale:', backendUpdates);
      
      console.log('🌐 Invio richiesta PUT a:', `${API_BASE_URL}/clients/${id}`);
      console.log('📦 Body della richiesta:', JSON.stringify(backendUpdates, null, 2));
      
      const response = await fetch(`${API_BASE_URL}/clients/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(backendUpdates)
      });

      console.log('📡 Risposta del backend:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('❌ Errore del backend:', errorData);
        throw new Error(errorData.message || 'Failed to update client');
      }

      const responseData = await response.json();
      console.log('✅ Risposta di successo dal backend:', responseData);

      dispatch({
        type: 'UPDATE_CLIENTE',
        payload: {
          id,
          ...updates
        }
      });

      toast.success('Cliente aggiornato con successo');
    } catch (error) {
      console.error('Error updating client:', error);
      toast.error('Errore durante l\'aggiornamento del cliente');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // LEGACY: stato settimanale di pubblicazione per giorno della settimana.
  // Il Calendario Editoriale usa ora le spunte per DATA
  // (GET /clients/publication-days + PATCH con `date`), che non si azzerano
  // ogni domenica. Qui resta solo la vecchia scrittura senza data.
  // `channels` è l'elenco dei canali confermati per quel giorno
  // (stato desiderato dopo l'azione): vuoto = giorno azzerato.
  // Aggiornamento ottimistico: aggiorna subito lo stato locale, poi persiste.
  const setPubblicazioneSettimanale = async (id: string, day: string, channels: string[]) => {
    const cliente = state.clienti.find(c => c.id === id);
    const prevGiorni = cliente?.pubblicazioneSettimanaleGiorni || [];
    const prevCanali = cliente?.pubblicazioneSettimanaleCanali || {};
    const richiesti = cliente?.piattaforme || [];

    // "Completo" = tutti i canali del cliente sono stati confermati.
    const full = richiesti.length > 0 && richiesti.every(c => channels.includes(c));

    // Nuovo stato canali per giorno (rimuovo la chiave se non resta nulla).
    const nextCanali: Record<string, string[]> = { ...prevCanali };
    if (channels.length === 0) {
      delete nextCanali[day];
    } else {
      nextCanali[day] = channels;
    }

    // Il giorno è "completo" (verde) solo se full.
    const nextGiorni = full
      ? Array.from(new Set([...prevGiorni, day]))
      : prevGiorni.filter(g => g !== day);

    // Update ottimistico (senza re-sort, non tocca updatedAt)
    dispatch({ type: 'SET_WEEKLY_PUBLISHED', payload: { id, giorni: nextGiorni, canali: nextCanali } });
    try {
      const response = await fetch(`${API_BASE_URL}/clients/${id}/weekly-published`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({ day, channels, full })
      });

      if (!response.ok) {
        throw new Error('Failed to update weekly publish state');
      }
    } catch (error) {
      console.error('Error updating weekly publish state:', error);
      // Rollback in caso di errore
      dispatch({ type: 'SET_WEEKLY_PUBLISHED', payload: { id, giorni: prevGiorni, canali: prevCanali } });
      toast.error('Errore aggiornamento pubblicazione');
      throw error;
    }
  };

  const archiveCliente = async (clienteId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/clients/${clienteId}/archive`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to archive client');
      }

      dispatch({
        type: 'UPDATE_CLIENTE',
        payload: {
          id: clienteId,
          status: 'archived'
        }
      });

      toast.success('Cliente archiviato con successo');
    } catch (error) {
      console.error('Error archiving client:', error);
      toast.error('Errore durante l\'archiviazione del cliente');
    }
  };

  const deleteCliente = async (id: string) => {
    console.log('🗑️ deleteCliente chiamata con ID:', id);
    try {
      setIsLoading(true);
      const url = `${API_BASE_URL}/clients/${id}`;
      console.log('🌐 Chiamando API DELETE:', url);
      console.log('🔑 Token presente:', !!getToken());
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      console.log('📡 Risposta API:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('❌ Errore API:', errorData);
        throw new Error(errorData.message || 'Failed to delete client');
      }

      const responseData = await response.json().catch(() => null);
      console.log('✅ Risposta API completata:', responseData);

      dispatch({
        type: 'DELETE_CLIENTE',
        payload: id
      });

      console.log('🔄 Dispatch DELETE_CLIENTE completato per ID:', id);
      toast.success('Cliente eliminato con successo');
    } catch (error) {
      console.error('🚨 Errore completo in deleteCliente:', error);
      toast.error('Errore durante l\'eliminazione del cliente');
      throw error;
    } finally {
      setIsLoading(false);
      console.log('🏁 deleteCliente terminato per ID:', id);
    }
  };

  const addContenuto = async (contenutoData: Omit<Contenuto, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      console.log('📝 addContenuto chiamata con:', contenutoData);
      
      // Send debug log to MCP server
      try {
        await fetch('http://localhost:8080/debug/log', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            level: 'info',
            message: 'addContenuto chiamata',
            data: contenutoData
          })
        });
      } catch (debugError) {
        // Ignore debug server errors
      }
      
      // Map frontend data to backend format
      const backendData: Record<string, any> = {
        client_id: contenutoData.clienteId,
        content_type: contenutoData.tipo === 'post' ? 'post' : 'reel',
        post_formato: contenutoData.tipo === 'post' ? (contenutoData.formatoPost === 'foto' ? 'foto' : 'grafica') : null,
        target_accounts: contenutoData.accountDestinazione || [],
        title: contenutoData.titolo,
        hook: contenutoData.hook || '',
        body: contenutoData.body || '',
        cta: contenutoData.cta || '',
        inspiration_link: contenutoData.linkIspirazione,
        status: mapStatusToBackend(contenutoData.stato) || 'draft'
      };
      if (contenutoData.copertinaUrl) backendData.thumbnail_url = contenutoData.copertinaUrl;
      if (contenutoData.copyPost) backendData.post_copy = contenutoData.copyPost;
      backendData.campaign_adv = contenutoData.campagnaAdv ?? false;

      const response = await authenticatedFetch(`${API_BASE_URL}/contents`, {
        method: 'POST',
        body: JSON.stringify(backendData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('Backend error:', errorData);

        // Log error to MCP server
        try {
          await fetch('http://localhost:8080/debug/log', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              level: 'error',
              message: 'addContenuto API error',
              data: { status: response.status, error: errorData }
            })
          });
        } catch (debugError) {}

        throw new Error(errorData.message || 'Failed to add content');
      }

      const data = await response.json();
      
      // Map backend response to frontend format
      const frontendContent: Contenuto = {
        id: data.data.id,
        clienteId: data.data.client_id,
        tipo: data.data.content_type === 'post' ? 'post' : 'reel', formatoPost: data.data.post_formato === 'foto' ? 'foto' : 'grafica',
        accountDestinazione: data.data.target_accounts || [],
        titolo: data.data.title,
        hook: data.data.hook,
        body: data.data.body,
        cta: data.data.cta,
        linkIspirazione: data.data.inspiration_link,
        stato: mapStatusToFrontend(data.data.status),
        note: data.data.notes,
        linkMontaggio: data.data.edited_video_url,
        linkPubblicato: data.data.published_links?.main,
        dataPubblicazione: data.data.published_date ? new Date(data.data.published_date) : undefined,
        videoRegistrato: data.data.video_recorded ?? false,
        videoRegistratoIl: data.data.video_recorded_at ? new Date(data.data.video_recorded_at) : undefined,
        videoMontato: data.data.video_edited ?? false,
        campagnaAdv: data.data.campaign_adv ?? false,
        advUtilizzato: data.data.adv_used ?? false,
        clipCaricate: data.data.clip_uploaded ?? false,
        copertinaUrl: data.data.thumbnail_url,
        copyPost: data.data.post_copy,
        createdAt: new Date(data.data.created_at),
        updatedAt: new Date(data.data.updated_at),
        clientModifiedAt: data.data.client_modified_at ? new Date(data.data.client_modified_at) : undefined,
        driveFlag: data.data.drive_flag ?? null,
        driveCartella: data.data.drive_cartella ?? null, montatoDa: data.data.montato_da ?? null, montatoLink: data.data.montato_link ?? null,
        ...campiPubblica(data.data)
      };

      dispatch({
        type: 'ADD_CONTENUTO',
        payload: frontendContent
      });

      toast.success('Contenuto aggiunto con successo');
    } catch (error) {
      console.error('Error adding content:', error);
      toast.error('Errore durante l\'aggiunta del contenuto');
      throw error;
    }
  };

  const updateContenuto = async (id: string, updates: Partial<Contenuto>) => {
    try {
      console.log('✏️ updateContenuto chiamata con:', { 
        id, 
        updates,
        dataPubblicazione: updates.dataPubblicazione,
        dataPubblicazioneType: typeof updates.dataPubblicazione,
        dataPubblicazioneISO: updates.dataPubblicazione?.toISOString(),
        dataPubblicazioneString: updates.dataPubblicazione ? new Date(updates.dataPubblicazione).toISOString() : null
      });
      
      // Send debug log to MCP server
      try {
        await fetch('http://localhost:8080/debug/log', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            level: 'info',
            message: 'updateContenuto chiamata',
            data: { id, updates }
          })
        });
      } catch (debugError) {
        // Ignore debug server errors
      }
      
      // Verifica che il contenuto esista
      const currentContent = state.contenuti.find(c => c.id === id);
      if (!currentContent) {
        console.error('❌ Contenuto non trovato per aggiornamento:', id);
        throw new Error('Contenuto non trovato');
      }
      
      console.log('🔍 Contenuto corrente trovato:', {
        id: currentContent.id,
        titolo: currentContent.titolo,
        dataPubblicazione: currentContent.dataPubblicazione,
        dataPubblicazioneISO: currentContent.dataPubblicazione?.toISOString(),
        dataPubblicazioneString: currentContent.dataPubblicazione ? format(currentContent.dataPubblicazione, 'dd/MM/yyyy HH:mm') : null
      });
      
      // NON fare l'ottimistic update per evitare conflitti con le date
      // Aspettiamo la risposta dal backend
      
      // Map frontend updates to backend format
      const backendUpdates: any = {};
      
      console.log('🔍 Debug updates object:', updates);
      console.log('🔍 dataPubblicazione value:', updates.dataPubblicazione);
      console.log('🔍 dataPubblicazione type:', typeof updates.dataPubblicazione);
      console.log('🔍 dataPubblicazione !== undefined:', updates.dataPubblicazione !== undefined);
      
      if (updates.tipo !== undefined) backendUpdates.content_type = updates.tipo;
      if (updates.formatoPost !== undefined) backendUpdates.post_formato = updates.formatoPost;
      if (updates.accountDestinazione !== undefined) backendUpdates.target_accounts = updates.accountDestinazione;
      if (updates.titolo !== undefined) backendUpdates.title = updates.titolo;
      if (updates.hook !== undefined) backendUpdates.hook = updates.hook;
      if (updates.body !== undefined) backendUpdates.body = updates.body;
      if (updates.cta !== undefined) backendUpdates.cta = updates.cta;
      if (updates.linkIspirazione !== undefined) backendUpdates.inspiration_link = updates.linkIspirazione;
      if (updates.stato !== undefined) backendUpdates.status = mapStatusToBackend(updates.stato);
      if (updates.note !== undefined) backendUpdates.notes = updates.note;
      if (updates.campagnaAdv !== undefined) backendUpdates.campaign_adv = updates.campagnaAdv;
      if (updates.advUtilizzato !== undefined) backendUpdates.adv_used = updates.advUtilizzato;
      if (updates.clipCaricate !== undefined) backendUpdates.clip_uploaded = updates.clipCaricate;
      if (updates.videoMontato !== undefined) backendUpdates.video_edited = updates.videoMontato;
      if (updates.prontoDaProgrammare !== undefined) backendUpdates.ready_to_schedule = updates.prontoDaProgrammare;
      if (updates.rimossoDaProgrammazione !== undefined) backendUpdates.removed_from_scheduling = updates.rimossoDaProgrammazione;
      
      // Handle dataPubblicazione more carefully
      if (updates.hasOwnProperty('dataPubblicazione')) {
        console.log('✅ dataPubblicazione property exists in updates');
        console.log('🔍 DEBUG dataPubblicazione received:', {
          value: updates.dataPubblicazione,
          type: typeof updates.dataPubblicazione,
          constructor: updates.dataPubblicazione?.constructor?.name,
          month: updates.dataPubblicazione instanceof Date ? updates.dataPubblicazione.getMonth() + 1 : 'N/A',
          monthName: updates.dataPubblicazione instanceof Date ? updates.dataPubblicazione.toLocaleString('it', { month: 'long' }) : 'N/A',
          toString: updates.dataPubblicazione?.toString(),
        });

        if (updates.dataPubblicazione === null || updates.dataPubblicazione === undefined) {
          // Use the new cancel-scheduling endpoint
          console.log('✅ Using cancel-scheduling endpoint to clear publication date');
          
          // Make a separate call to cancel scheduling
          const cancelResponse = await fetch(`${API_BASE_URL}/contents/${id}/cancel-scheduling`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${getToken()}`
            },
            body: JSON.stringify({
              notes: updates.note || 'Programmazione annullata'
            })
          });

          if (!cancelResponse.ok) {
            const errorData = await cancelResponse.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(errorData.message || 'Failed to cancel scheduling');
          }

          // Get the updated content from the response
          const cancelData = await cancelResponse.json();
          if (cancelData.data) {
            const updatedContent: Contenuto = {
              id: cancelData.data.id,
              clienteId: currentContent?.clienteId || '',
              tipo: currentContent?.tipo,
              formatoPost: currentContent?.formatoPost,
              accountDestinazione: currentContent?.accountDestinazione,
              titolo: currentContent?.titolo || '',
              hook: currentContent?.hook || '',
              body: currentContent?.body || '',
              cta: currentContent?.cta || '',
              linkIspirazione: currentContent?.linkIspirazione,
              stato: mapStatusToFrontend(cancelData.data.status),
              note: cancelData.data.notes,
              linkMontaggio: currentContent?.linkMontaggio,
              linkPubblicato: currentContent?.linkPubblicato,
              dataPubblicazione: cancelData.data.published_date ? new Date(cancelData.data.published_date) : undefined,
              videoRegistrato: cancelData.data.video_recorded ?? currentContent?.videoRegistrato ?? false,
              videoRegistratoIl: cancelData.data.video_recorded_at ? new Date(cancelData.data.video_recorded_at) : undefined,
              videoMontato: cancelData.data.video_edited ?? currentContent?.videoMontato ?? false,
              prontoDaProgrammare: cancelData.data.ready_to_schedule ?? currentContent?.prontoDaProgrammare ?? false,
              rimossoDaProgrammazione: cancelData.data.removed_from_scheduling ?? currentContent?.rimossoDaProgrammazione ?? false,
              campagnaAdv: cancelData.data.campaign_adv ?? currentContent?.campagnaAdv ?? false,
              advUtilizzato: cancelData.data.adv_used ?? currentContent?.advUtilizzato ?? false,
              clipCaricate: cancelData.data.clip_uploaded ?? currentContent?.clipCaricate ?? false,
              copertinaUrl: currentContent?.copertinaUrl,
              copyPost: currentContent?.copyPost,
              createdAt: currentContent?.createdAt || new Date(),
              updatedAt: new Date()
            };

            console.log('🔄 Updated content after cancellation:', updatedContent);
            console.log('📅 dataPubblicazione after cancellation:', updatedContent.dataPubblicazione);

            dispatch({
              type: 'UPDATE_CONTENUTO',
              payload: updatedContent
            });

            return;
          }
        } else {
          // ⚠️ TEMPORARY SEPTEMBER FIX: Force correct date if it was incorrectly converted to August
          let correctedDate = updates.dataPubblicazione;

          console.log('🚨 SEPTEMBER FIX DEBUG - Original date received:', {
            originalDate: updates.dataPubblicazione,
            month: updates.dataPubblicazione.getMonth() + 1,
            monthName: updates.dataPubblicazione.toLocaleString('it', { month: 'long' }),
            day: updates.dataPubblicazione.getDate(),
            hours: updates.dataPubblicazione.getHours()
          });

          // Check if this looks like an August date that should be September
          // SEPTEMBER BUG FIX: Any August date in 2025 might actually be September that was incorrectly converted
          if (updates.dataPubblicazione.getMonth() === 7 && updates.dataPubblicazione.getFullYear() === 2025) {
            // This is likely the September->August bug, correct it back to September
            correctedDate = new Date(
              updates.dataPubblicazione.getFullYear(),
              8, // September (0-indexed)
              updates.dataPubblicazione.getDate(),
              updates.dataPubblicazione.getHours(),
              updates.dataPubblicazione.getMinutes(),
              updates.dataPubblicazione.getSeconds(),
              updates.dataPubblicazione.getMilliseconds()
            );

            console.log('✅ SEPTEMBER FIX APPLIED - Corrected ALL August 2025 dates to September:', {
              originalDate: updates.dataPubblicazione,
              correctedDate,
              originalMonth: updates.dataPubblicazione.getMonth() + 1,
              correctedMonth: correctedDate.getMonth() + 1,
              originalMonthName: updates.dataPubblicazione.toLocaleString('it', { month: 'long' }),
              correctedMonthName: correctedDate.toLocaleString('it', { month: 'long' }),
              day: correctedDate.getDate(),
              hours: correctedDate.getHours()
            });
          }

          // Use the corrected date
          console.log('🔍 DEBUG Before formatDateForBackend:', {
            input: correctedDate,
            month: correctedDate.getMonth() + 1,
            monthName: correctedDate.toLocaleString('it', { month: 'long' })
          });

          const localDateString = formatDateForBackend(correctedDate);

          console.log('🔍 DEBUG After formatDateForBackend:', {
            output: localDateString,
            extractedMonth: localDateString.split('-')[1]
          });

          backendUpdates.published_date = localDateString;
          console.log('✅ Final published_date sent to backend:', localDateString);
        }
      } else {
        console.log('❌ dataPubblicazione property not in updates, skipping');
      }
      
      if (updates.linkMontaggio !== undefined) {
        backendUpdates.edited_video_url = updates.linkMontaggio;
      }
      if (updates.copertinaUrl !== undefined) {
        backendUpdates.thumbnail_url = updates.copertinaUrl;
      }
      if (updates.copyPost !== undefined) {
        backendUpdates.post_copy = updates.copyPost;
      }

      console.log('📤 Backend updates:', backendUpdates);
      console.log('🔗 Request URL:', `${API_BASE_URL}/contents/${id}`);
      console.log('🔑 Token:', getToken() ? 'Present' : 'Missing');
      console.log('📋 Request body:', JSON.stringify(backendUpdates, null, 2));
      console.log('🔍 dataPubblicazione nel backendUpdates:', {
        hasDataPubblicazione: 'dataPubblicazione' in backendUpdates,
        dataPubblicazioneValue: backendUpdates.dataPubblicazione,
        dataPubblicazioneType: typeof backendUpdates.dataPubblicazione
      });
      
      const response = await fetch(`${API_BASE_URL}/contents/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(backendUpdates)
      });

      if (!response.ok) {
        // Rollback ottimistic update in caso di errore
        if (currentContent) {
          dispatch({
            type: 'UPDATE_CONTENUTO',
            payload: currentContent
          });
        }
        
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('Backend error:', errorData);
        console.error('Response status:', response.status);
        console.error('Response headers:', Object.fromEntries(response.headers.entries()));
        console.error('Request payload:', backendUpdates);
        
        // Log detailed validation errors if available
        if (errorData.errors && Array.isArray(errorData.errors)) {
          console.error('🔍 Validation errors:', errorData.errors);
          errorData.errors.forEach((error: any, index: number) => {
            console.error(`🔍 Error ${index + 1}:`, error);
          });
        }
        
        // Log error to MCP server
        try {
          await fetch('http://localhost:8080/debug/log', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              level: 'error',
              message: 'updateContenuto API error',
              data: { 
                status: response.status, 
                error: errorData, 
                id, 
                updates,
                backendUpdates,
                requestUrl: `${API_BASE_URL}/contents/${id}`
              }
            })
          });
        } catch (debugError) {}
        
        throw new Error(errorData.message || 'Failed to update content');
      }

      // Conferma l'aggiornamento con i dati dal backend
      const data = await response.json();
      console.log('📥 Risposta dal backend:', {
        success: data.success,
        data: data.data,
        published_date: data.data?.published_date,
        published_dateType: typeof data.data?.published_date
      });
      
      if (data.data) {
        // Handle the case where backend doesn't return published_date in response
        // but we know we sent it in the request
        let finalDataPubblicazione: Date | undefined = undefined;
        
        if (data.data.published_date) {
          // Backend returned the published_date, use it
          finalDataPubblicazione = new Date(data.data.published_date);
        } else if (updates.dataPubblicazione && backendUpdates.published_date) {
          // Backend didn't return published_date but we sent it
          // Use the corrected date from our fix, not the original buggy date
          const sentDateString = backendUpdates.published_date;
          if (sentDateString) {
            const [datePart, timePart] = sentDateString.split(' ');
            const [year, month, day] = datePart.split('-').map(Number);
            const [hours, minutes, seconds] = timePart.split(':').map(Number);
            finalDataPubblicazione = new Date(year, month - 1, day, hours, minutes || 0, seconds || 0);
            console.log('✅ Using corrected date from backend request:', finalDataPubblicazione);
          } else {
            console.log('⚠️ Backend didn\'t return published_date, using original value:', updates.dataPubblicazione);
            finalDataPubblicazione = updates.dataPubblicazione;
          }
        }
        
        const updatedContent: Contenuto = {
          id: data.data.id,
          clienteId: data.data.client_id,
          tipo: data.data.content_type === 'post' ? 'post' : 'reel', formatoPost: data.data.post_formato === 'foto' ? 'foto' : 'grafica',
          accountDestinazione: data.data.target_accounts || [],
          titolo: data.data.title || '',
          hook: data.data.hook || '',
          body: data.data.body || '',
          cta: data.data.cta || '',
          linkIspirazione: data.data.inspiration_link,
          stato: mapStatusToFrontend(data.data.status) || 'bozza',
          note: data.data.notes,
          linkMontaggio: data.data.edited_video_url,
          linkPubblicato: data.data.published_links?.main,
          dataPubblicazione: finalDataPubblicazione,
          videoRegistrato: data.data.video_recorded ?? false,
          videoRegistratoIl: data.data.video_recorded_at ? new Date(data.data.video_recorded_at) : undefined,
          videoMontato: data.data.video_edited ?? false,
          prontoDaProgrammare: data.data.ready_to_schedule ?? false,
          rimossoDaProgrammazione: data.data.removed_from_scheduling ?? false,
          campagnaAdv: data.data.campaign_adv ?? false,
          advUtilizzato: data.data.adv_used ?? false,
          clipCaricate: data.data.clip_uploaded ?? false,
          copertinaUrl: data.data.thumbnail_url,
          copyPost: data.data.post_copy,
          createdAt: new Date(data.data.created_at),
          updatedAt: new Date(data.data.updated_at),
          clientModifiedAt: data.data.client_modified_at ? new Date(data.data.client_modified_at) : undefined,
          driveFlag: data.data.drive_flag ?? null,
          driveCartella: data.data.drive_cartella ?? null, montatoDa: data.data.montato_da ?? null, montatoLink: data.data.montato_link ?? null,
          ...campiPubblica(data.data)
        };

        console.log('🔄 Contenuto aggiornato dal backend:', {
          id: updatedContent.id,
          dataPubblicazione: updatedContent.dataPubblicazione,
          dataPubblicazioneISO: updatedContent.dataPubblicazione?.toISOString(),
          dataPubblicazioneString: updatedContent.dataPubblicazione ? format(updatedContent.dataPubblicazione, 'dd/MM/yyyy HH:mm') : null
        });
        
        dispatch({
          type: 'UPDATE_CONTENUTO',
          payload: updatedContent
        });
      }

      toast.success('Contenuto aggiornato con successo');
    } catch (error) {
      console.error('Error updating content:', error);
      toast.error('Errore durante l\'aggiornamento del contenuto');
      throw error;
    }
  };

  const deleteContenuto = async (id: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/contents/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || 'Failed to delete content');
      }

      dispatch({
        type: 'DELETE_CONTENUTO',
        payload: id
      });

      toast.success('Contenuto eliminato con successo');
    } catch (error) {
      console.error('Error deleting content:', error);
      toast.error('Errore durante l\'eliminazione del contenuto');
      throw error;
    }
  };

  const updateContenutoStatus = async (id: string, status: StatoContenuto, notes?: string) => {
    try {
      // Ottimistic update
      const currentContent = state.contenuti.find(c => c.id === id);
      if (currentContent) {
        dispatch({
          type: 'UPDATE_CONTENUTO',
          payload: {
            ...currentContent,
            stato: status,
            note: notes,
            updatedAt: new Date()
          } as Contenuto
        });
      }
      
      const backendStatus = mapStatusToBackend(status);
      
      const response = await fetch(`${API_BASE_URL}/contents/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({ status: backendStatus, notes })
      });

      if (!response.ok) {
        // Rollback in caso di errore
        if (currentContent) {
          dispatch({
            type: 'UPDATE_CONTENUTO',
            payload: currentContent
          });
        }
        
        const errorData = await response.json();
        console.error('Backend error:', errorData);
        throw new Error('Failed to update content status');
      }

      toast.success('Stato del contenuto aggiornato con successo');
    } catch (error) {
      console.error('Error updating content status:', error);
      toast.error('Errore durante l\'aggiornamento dello stato del contenuto');
      throw error;
    }
  };

  const loadContenuti = useCallback(async () => {
    // Evita chiamate duplicate, ma ricorda di ricaricare alla fine.
    if (contenutiInCaricamento.current) {
      ricaricaContenutiDopo.current = true;
      return;
    }
    
    try {
      contenutiInCaricamento.current = true;
      setIsLoadingContenuti(true);
      
      crashLogger.logEvent('API_CALL_START', {
        endpoint: `${API_BASE_URL}/contents`,
        method: 'GET',
        hasToken: !!getToken()
      });

      const response = await authenticatedFetch(`${API_BASE_URL}/contents?limit=1000`);

      crashLogger.logEvent('API_CALL_RESPONSE', {
        endpoint: `${API_BASE_URL}/contents`,
        status: response.status,
        ok: response.ok
      });

      if (!response.ok) {
        crashLogger.logEvent('API_CALL_ERROR', {
          endpoint: `${API_BASE_URL}/contents`,
          status: response.status,
          statusText: response.statusText
        });
        throw new Error('Failed to load contents');
      }

      const data = await response.json();

      // Debug: Show total contents received and specifically archived ones
      console.log(`🔍 DATACONTEXT - Total contenuti ricevuti dal backend: ${(data.data || []).length}`);

      // Debug per TESTO_PRONTO
      const testoProontoBackend = (data.data || []).filter((c: any) => c.status === 'text_ready');
      console.log(`🔍 DATACONTEXT - Contenuti text_ready dal backend:`, testoProontoBackend.length);
      console.log(`🔍 DATACONTEXT - Dettagli text_ready:`, testoProontoBackend.map((c: any) => ({
        title: c.title,
        status: c.status,
        client_id: c.client_id,
        created_at: c.created_at,
        mese: new Date(c.created_at).getMonth() + 1,
        anno: new Date(c.created_at).getFullYear()
      })));

      const archivedContents = (data.data || []).filter((c: any) => c.status === 'archived');
      console.log(`🔍 DATACONTEXT - Contenuti archiviati ricevuti:`, archivedContents.map((c: any) => ({ title: c.title, status: c.status, client_id: c.client_id })));
      
      // Map backend data to frontend format
      const transformedContents = (data.data || []).map((content: any) => {
        const mappedStatus = mapStatusToFrontend(content.status);
        console.log(`🔍 Status mapping: ${content.title} - Backend: "${content.status}" → Frontend: "${mappedStatus}"`);
        
        return {
          id: content.id,
          clienteId: content.client_id,
          tipo: content.content_type === 'post' ? 'post' : 'reel', formatoPost: content.post_formato === 'foto' ? 'foto' : 'grafica',
          accountDestinazione: content.target_accounts || [],
          titolo: content.title || '',
          hook: content.hook || '',
          body: content.body || '',
          cta: content.cta || '',
          linkIspirazione: content.inspiration_link,
          stato: mappedStatus || 'bozza',
          note: content.notes,
          linkMontaggio: content.edited_video_url,
          linkPubblicato: content.published_links?.main,
          dataPubblicazione: content.published_date ? new Date(content.published_date) : undefined,
          createdAt: new Date(content.created_at),
          videoRegistrato: content.video_recorded ?? false,
          videoRegistratoIl: content.video_recorded_at ? new Date(content.video_recorded_at) : undefined,
          videoMontato: content.video_edited ?? false,
          prontoDaProgrammare: content.ready_to_schedule ?? false,
          rimossoDaProgrammazione: content.removed_from_scheduling ?? false,
          campagnaAdv: content.campaign_adv ?? false,
          uscitaMancata: content.missed_publish_at ? new Date(content.missed_publish_at) : undefined,
          uscitaMancataMotivo: content.missed_publish_reason || undefined,
          advUtilizzato: content.adv_used ?? false,
          clipCaricate: content.clip_uploaded ?? false,
          copertinaUrl: content.thumbnail_url,
          copyPost: content.post_copy,
          updatedAt: new Date(content.updated_at),
          clientModifiedAt: content.client_modified_at ? new Date(content.client_modified_at) : undefined,
          driveFlag: content.drive_flag ?? null,
          driveCartella: content.drive_cartella ?? null, montatoDa: content.montato_da ?? null, montatoLink: content.montato_link ?? null,
          ...campiPubblica(content)
        };
      });

      dispatch({
        type: 'SET_CONTENUTI',
        payload: transformedContents
      });
    } catch (error) {
      console.error('Error loading contents:', error);
      toast.error('Errore durante il caricamento dei contenuti');
    } finally {
      contenutiInCaricamento.current = false;
      setIsLoadingContenuti(false);
      if (ricaricaContenutiDopo.current) {
        ricaricaContenutiDopo.current = false;
        void loadContenutiRef.current();
      }
    }
  }, [getToken]);
  loadContenutiRef.current = loadContenuti;

  const loadContenutiFiltered = useCallback(async (filters: ContentFilters) => {
    try {
      const params = new URLSearchParams();
      if (filters.clientId) params.append('client_id', filters.clientId);
      if (filters.status) params.append('status', filters.status);
      if (filters.dateFrom) params.append('date_from', filters.dateFrom.toISOString());
      if (filters.dateTo) params.append('date_to', filters.dateTo.toISOString());

      const response = await fetch(`${API_BASE_URL}/contents?${params}`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load filtered contents');
      }

      const data = await response.json();
      dispatch({
        type: 'SET_CONTENUTI',
        payload: data.data
      });
    } catch (error) {
      console.error('Error loading filtered contents:', error);
      toast.error('Errore durante il caricamento dei contenuti filtrati');
    }
  }, [getToken]);

  const getClienteById = useCallback((id: string): Cliente | undefined => {
    return state.clienti.find(cliente => cliente.id === id);
  }, [state.clienti]);

  const loadDashboardStats = async () => {
    try {
      console.log('🔍 DEBUG API - Chiamando /dashboard/stats...');

      const response = await fetch(`${API_BASE_URL}/dashboard/stats`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      console.log('🔍 DEBUG API - Response status:', response.status);

      if (!response.ok) {
        console.log('🔍 DEBUG API - Response NON OK, usando calcoli locali');
        throw new Error('Failed to load dashboard stats');
      }

      const data = await response.json();

      console.log('🔍 DEBUG API - Dashboard stats dal backend:', {
        success: data.success,
        data: data.data,
        active_clients: data.data?.active_clients,
        monthly_reels_target: data.data?.monthly_reels_target,
        reels_completed: data.data?.reels_completed,
        reels_published: data.data?.reels_published,
        client_progress: data.data?.client_progress
      });

      dispatch({
        type: 'SET_DASHBOARD_STATS',
        payload: data.data
      });
    } catch (error) {
      console.error('🚨 Error loading dashboard stats:', error);
      console.log('📊 Usando calcoli locali invece delle statistiche backend');
      // Non mostrare errore toast se è un 404 (endpoint non implementato)
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (!errorMessage.includes('404')) {
        toast.error('Errore durante il caricamento delle statistiche dashboard');
      }
    }
  };

  const publishContenuto = async (id: string, linkPubblicato: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/contents/${id}/publish`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({ 
          published_links: { main: linkPubblicato },
          published_date: formatDateForBackend(new Date())
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('Backend error:', errorData);
        throw new Error('Failed to publish content');
      }

      const data = await response.json();
      
      // Usa i dati dal backend per aggiornare lo stato
      if (data.success && data.data) {
        dispatch({
          type: 'UPDATE_CONTENUTO',
          payload: {
            id,
            linkPubblicato: data.data.published_links?.main || linkPubblicato,
            stato: StatoContenuto.PUBBLICATO,
            dataPubblicazione: data.data.published_date ? new Date(data.data.published_date) : new Date()
          } as Contenuto
        });
      } else {
        // Fallback in caso di risposta senza dati
        dispatch({
          type: 'UPDATE_CONTENUTO',
          payload: {
            id,
            linkPubblicato,
            stato: StatoContenuto.PUBBLICATO,
            dataPubblicazione: new Date()
          } as Contenuto
        });
      }

      toast.success('Contenuto pubblicato con successo');
    } catch (error) {
      console.error('Error publishing content:', error);
      toast.error('Errore durante la pubblicazione del contenuto');
      throw error;
    }
  };

  const cancelScheduling = async (id: string, notes?: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/contents/${id}/cancel-scheduling`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          notes: notes || 'Programmazione annullata'
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || 'Failed to cancel scheduling');
      }

      const data = await response.json();
      
      if (data.data) {
        const currentContent = state.contenuti.find(c => c.id === id);
        if (currentContent) {
          const newStatus = mapStatusToFrontend(data.data.status);
          
          // MANTIENI la data di pubblicazione se il contenuto era già pubblicato e ora viene archiviato
          const shouldKeepDate = currentContent.stato === StatoContenuto.PUBBLICATO && 
                                newStatus === StatoContenuto.ARCHIVIATO;
          
          const updatedContent: Contenuto = {
            ...currentContent,
            stato: newStatus,
            note: data.data.notes,
            // Solo rimuovi la data se è un vero annullamento di programmazione (non archiviazione)
            dataPubblicazione: shouldKeepDate ? currentContent.dataPubblicazione : undefined,
            updatedAt: new Date()
          };

          dispatch({
            type: 'UPDATE_CONTENUTO',
            payload: updatedContent
          });
        }
      }

      toast.success('Programmazione annullata con successo');
    } catch (error) {
      console.error('Error canceling scheduling:', error);
      toast.error('Errore durante l\'annullamento della programmazione');
      throw error;
    }
  };

  // Toglie un contenuto dall'archivio riportandolo tra gli attuali in "Testo pronto".
  // Usa lo stesso endpoint di cancel-scheduling (che azzera scheduled_date /
  // published_date e imposta status='text_ready'), così il contenuto non resta
  // agganciato a una vecchia data in calendario e rientra nel normale flusso.
  // «È uscito»: il contenuto segnalato come uscita mancata era uscito davvero.
  // Il backend lo archivia con la data in cui doveva uscire.
  const confermaUscita = async (id: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/contents/${id}/conferma-uscita`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        }
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || 'Conferma uscita fallita');
      }
      const data = await response.json();
      const currentContent = state.contenuti.find(c => c.id === id);
      if (currentContent) {
        dispatch({
          type: 'UPDATE_CONTENUTO',
          payload: {
            ...currentContent,
            stato: StatoContenuto.ARCHIVIATO,
            dataPubblicazione: data.data?.published_date ? new Date(data.data.published_date) : currentContent.uscitaMancata,
            uscitaMancata: undefined,
            uscitaMancataMotivo: undefined,
            updatedAt: new Date()
          } as Contenuto
        });
      }
      toast.success('Segnato come uscito: ora è in archivio');
    } catch (error) {
      console.error('Errore conferma uscita:', error);
      toast.error('Errore nel segnare il contenuto come uscito');
      throw error;
    }
  };

  const unarchiveContenuto = async (id: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/contents/${id}/cancel-scheduling`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({ notes: 'Ripristinato dall\'archivio' })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || 'Failed to unarchive content');
      }

      const data = await response.json();
      const currentContent = state.contenuti.find(c => c.id === id);

      if (currentContent) {
        dispatch({
          type: 'UPDATE_CONTENUTO',
          payload: {
            ...currentContent,
            stato: data.data ? mapStatusToFrontend(data.data.status) : StatoContenuto.TESTO_PRONTO,
            note: data.data?.notes ?? currentContent.note,
            dataPubblicazione: undefined,
            linkPubblicato: undefined,
            updatedAt: new Date()
          } as Contenuto
        });
      }

      toast.success('Contenuto ripristinato: ora è tra gli attuali come "Testo confermato"');
    } catch (error) {
      console.error('Error unarchiving content:', error);
      toast.error('Errore durante il ripristino del contenuto dall\'archivio');
      throw error;
    }
  };

  const toggleVideoRecorded = async (id: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/contents/${id}/video-recorded`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to toggle video recorded');
      }

      const data = await response.json();

      const currentContent = state.contenuti.find(c => c.id === id);
      if (currentContent) {
        dispatch({
          type: 'UPDATE_CONTENUTO',
          payload: {
            ...currentContent,
            videoRegistrato: data.data.video_recorded,
            // togliere "Registrato" azzera anche "Montato" (vedi backend)
            videoMontato: data.data.video_edited ?? (data.data.video_recorded ? currentContent.videoMontato : false),
            updatedAt: new Date()
          }
        });
      }

      toast.success(data.data.video_recorded ? 'Video segnato come registrato!' : 'Video segnato come da registrare');
    } catch (error) {
      console.error('Error toggling video recorded:', error);
      toast.error('Errore nell\'aggiornamento dello stato di registrazione');
      throw error;
    }
  };

  // Carica i dati automaticamente quando l'utente è autenticato
  useEffect(() => {
    if (isAuthenticated && !dataLoaded && !isLoadingClienti && !isLoadingContenuti) {
      const loadInitialData = async () => {
        try {
          await Promise.all([
            loadClienti(),
            loadContenuti()
          ]);
          setDataLoaded(true);
        } catch (error) {
          console.error('Error loading initial data:', error);
          // Non fare nulla, le singole funzioni già gestiscono gli errori
        }
      };
      
      loadInitialData();
    }
  }, [isAuthenticated, dataLoaded, isLoadingClienti, isLoadingContenuti, loadClienti, loadContenuti]);
  
  // Refresh periodico dei dati ogni 2 minuti (ridotto da 30 secondi per evitare logging eccessivo)
  useEffect(() => {
    if (!isAuthenticated || !dataLoaded) return;
    
    const refreshInterval = setInterval(async () => {
      // Solo se non ci sono operazioni in corso
      if (!isLoadingClienti && !isLoadingContenuti) {
        try {
          await loadContenuti();
        } catch (error) {
          // Logga solo errori importanti, non ogni singolo fallimento
          if (error instanceof Error && !error.message.includes('401') && !error.message.includes('403')) {
            console.error('Error during periodic refresh:', error);
          }
        }
      }
    }, 120000); // 2 minuti invece di 30 secondi
    
    return () => clearInterval(refreshInterval);
  }, [isAuthenticated, dataLoaded, isLoadingClienti, isLoadingContenuti, loadContenuti]);

  const value = {
    state,
    dispatch,
    isLoading,
    addCliente,
    updateCliente,
    setPubblicazioneSettimanale,
    deleteCliente,
    archiveCliente,
    loadClienti,
    loadDashboardStats,
    addContenuto,
    updateContenuto,
    deleteContenuto,
    updateContenutoStatus,
    publishContenuto,
    cancelScheduling,
    unarchiveContenuto,
    confermaUscita,
    toggleVideoRecorded,
    loadContenuti,
    loadContenutiFiltered,
    getClienteById
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
}; 